import type { ReportsFindAllParams } from '@api/WhisApi/generated';
import {
  ExtendedReportFiltersParams,
  MultiSelectReportFilterField,
  ReportFiltersField,
  ReportFiltersSection,
  ReportFiltersValues,
} from './reportFilters.types';

const parseBooleanFilter = (value: string): boolean | undefined => {
  if (!['true', 'false'].includes(value)) {
    return undefined;
  }
  return value === 'true';
};

const toIsoDate = (value: Date | null): string | undefined =>
  value ? value.toISOString() : undefined;

const sanitizeString = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeStringArray = <T extends string>(
  values: T[],
): T[] | undefined => {
  const sanitized = values.filter((v) => v.trim().length > 0);
  return sanitized.length > 0 ? sanitized : undefined;
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();

  return leftSorted.every((value, index) => value === rightSorted[index]);
};

const sanitizeOption = <T extends string>(value: T | ''): T | undefined =>
  value === '' ? undefined : value;

const sanitizeOptionAsArray = <T extends string>(
  value: T | '',
): T[] | undefined => {
  const sanitized = sanitizeOption(value);
  return sanitized ? [sanitized] : undefined;
};

export const buildEmptyReportFilters = (): ReportFiltersValues => ({
  code: '',
  status: [],
  countryCode: [],
  regionId: [],
  detectionDateFrom: null,
  detectionDateTo: null,
  animalType: '',
  specie: '',
  wildType: '',
  surveillanceType: [],
  knownCause: '',
  etiologyTypeCode: [],
  etiologicalAgentCode: [],
  hasDiagnosticProcedure: '',
  categoryOfTest: [],
  sampleTypeCode: [],
});

export const hasActiveReportFilters = (
  values: ReportFiltersValues | undefined,
  emptyFilters: ReportFiltersValues,
): boolean => {
  const currentValues = values ?? emptyFilters;

  return (Object.keys(emptyFilters) as (keyof ReportFiltersValues)[]).some(
    (key) => {
      const currentValue = currentValues[key];
      const emptyValue = emptyFilters[key];

      if (currentValue instanceof Date) {
        return (
          !(emptyValue instanceof Date) ||
          currentValue.getTime() !== emptyValue.getTime()
        );
      }

      if (Array.isArray(currentValue) && Array.isArray(emptyValue)) {
        return !areStringArraysEqual(currentValue, emptyValue);
      }

      return currentValue !== emptyValue;
    },
  );
};

const isDateFilterActive = (
  value: Date | null,
  emptyValue: Date | null,
): boolean => {
  if (value === null) {
    return emptyValue !== null;
  }

  if (emptyValue === null) {
    return true;
  }

  return value.getTime() !== emptyValue.getTime();
};

const isStringFilterActive = (value: string, emptyValue: string): boolean => {
  if (value === emptyValue) {
    return false;
  }

  return value.trim().length > 0;
};

const isArrayFilterActive = (
  value: string[],
  emptyValue: string[],
): boolean => {
  return !areStringArraysEqual(value, emptyValue);
};

export const isReportFilterFieldActive = (
  field: ReportFiltersField,
  values: ReportFiltersValues | undefined,
  emptyFilters: ReportFiltersValues,
): boolean => {
  const currentValues = values ?? emptyFilters;

  if (field.type === 'date-range') {
    const fromValue = currentValues[field.idFrom];
    const toValue = currentValues[field.idTo];
    const emptyFromValue = emptyFilters[field.idFrom];
    const emptyToValue = emptyFilters[field.idTo];

    return (
      isDateFilterActive(fromValue, emptyFromValue) ||
      isDateFilterActive(toValue, emptyToValue)
    );
  }

  const value = currentValues[field.id];
  const emptyValue = emptyFilters[field.id];

  if (field.type === 'multi-select') {
    return isArrayFilterActive(value as string[], emptyValue as string[]);
  }

  return isStringFilterActive(value as string, emptyValue as string);
};

export const countActiveReportFilterFields = (
  section: ReportFiltersSection,
  values: ReportFiltersValues | undefined,
  emptyFilters: ReportFiltersValues,
): number =>
  section.fields.reduce(
    (count, field) =>
      count + (isReportFilterFieldActive(field, values, emptyFilters) ? 1 : 0),
    0,
  );

const resolveDynamicMultiSelectOptions = (
  field: MultiSelectReportFilterField,
  values: ReportFiltersValues,
): MultiSelectReportFilterField['options'] => {
  if (!field.dynamicOptions) {
    return field.options;
  }

  const {
    optionsByValue,
    dependsOn,
    showAllWhenDependencyEmpty = false,
  } = field.dynamicOptions;
  const dependencyValue = values[dependsOn];

  if (dependencyValue.length === 0) {
    return showAllWhenDependencyEmpty ? field.options : [];
  }
  if (Array.isArray(dependencyValue)) {
    const allowedValues = new Set(
      dependencyValue.flatMap(
        (value) => optionsByValue[value]?.map((option) => option.value) ?? [],
      ),
    );

    return field.options.filter((option) => allowedValues.has(option.value));
  }

  return optionsByValue[dependencyValue] ?? [];
};

export function resolveReportFilterField(
  field: MultiSelectReportFilterField,
  values: ReportFiltersValues,
): MultiSelectReportFilterField;
export function resolveReportFilterField(
  field: ReportFiltersField,
  values: ReportFiltersValues,
): ReportFiltersField;
export function resolveReportFilterField(
  field: ReportFiltersField,
  values: ReportFiltersValues,
): ReportFiltersField {
  if (field.type !== 'multi-select') {
    return field;
  }

  return {
    ...field,
    options: resolveDynamicMultiSelectOptions(field, values),
  };
}

export const sanitizeReportMultiSelectValue = (
  field: ReportFiltersField,
  values: ReportFiltersValues,
): string[] | null => {
  if (field.type !== 'multi-select') {
    return null;
  }

  const resolvedField = resolveReportFilterField(field, values);
  const currentValues = values[field.id] ?? [];
  const allowedValues = new Set(
    resolvedField.options.map((option) => option.value),
  );
  const sanitizedValues = currentValues.filter((value) =>
    allowedValues.has(value),
  );

  return sanitizedValues.length === currentValues.length
    ? null
    : sanitizedValues;
};

export const buildReportFiltersParams = (
  filters: ReportFiltersValues,
): Partial<ExtendedReportFiltersParams> => {
  const code = sanitizeString(filters.code);
  // TODO: Remove local extension once generated types include reportName.
  const status = sanitizeStringArray(filters.status);
  const countryCode = sanitizeStringArray(filters.countryCode);
  const regionId = sanitizeStringArray(filters.regionId);
  const animalType: ReportsFindAllParams['animalType'] = sanitizeOptionAsArray(
    filters.animalType,
  );
  const specie = sanitizeString(filters.specie);
  const wildType: ReportsFindAllParams['wildType'] = sanitizeOptionAsArray(
    filters.wildType,
  );
  const surveillanceType = sanitizeStringArray(filters.surveillanceType);
  const categoryOfTest = sanitizeStringArray(filters.categoryOfTest);

  return {
    code,
    status,
    countryCode,
    regionId,
    detectionDateFrom: toIsoDate(filters.detectionDateFrom),
    detectionDateTo: toIsoDate(filters.detectionDateTo),
    animalType,
    specie,
    wildType,
    surveillanceType,
    knownCause: parseBooleanFilter(filters.knownCause),
    etiologyTypeCode: sanitizeStringArray(filters.etiologyTypeCode),
    etiologicalAgentCode: sanitizeStringArray(filters.etiologicalAgentCode),
    hasDiagnosticProcedure: parseBooleanFilter(filters.hasDiagnosticProcedure),
    categoryOfTest,
    sampleTypeCode: sanitizeStringArray(filters.sampleTypeCode),
  };
};
